import { definePreset } from '@primeuix/themes';
import Aura from '@primeuix/themes/aura';

/**
 * PrimeUI (Aura) preset wired to the Spyglass palette.
 *
 * Palette CSS variables store full rgb() values, so no wrapping needed:
 *   --primeng-primary-500: rgb(50, 136, 125);
 *   usage: var(--primeng-primary-500)
 */
const PrimengPreset = definePreset(Aura, {
  primitive: {
    green: {
      50: 'rgb(237, 247, 245)',
      100: 'rgb(217, 242, 238)',
      200: 'rgb(156, 211, 204)',
      300: 'rgb(113, 178, 169)',
      400: 'rgb(81, 157, 147)',
      500: 'rgb(50, 136, 125)',
      600: 'rgb(40, 109, 100)',
      700: 'rgb(36, 84, 78)',
      800: 'rgb(26, 63, 58)',
      900: 'rgb(18, 46, 42)',
      950: 'rgb(10, 30, 27)',
    },
    orange: {
      50: 'rgb(255, 247, 237)',
      100: 'rgb(255, 237, 211)',
      200: 'rgb(254, 215, 170)',
      300: 'rgb(253, 186, 116)',
      400: 'rgb(251, 146, 60)',
      500: 'rgb(255, 128, 64)',
      600: 'rgb(255, 106, 46)',
      700: 'rgb(217, 119, 6)',
      800: 'rgb(154, 52, 18)',
      900: 'rgb(124, 45, 18)',
      950: 'rgb(69, 20, 7)',
    },
    red: {
      50: 'rgb(254, 242, 242)',
      100: 'rgb(254, 226, 226)',
      200: 'rgb(252, 202, 202)',
      300: 'rgb(252, 165, 165)',
      400: 'rgb(248, 113, 113)',
      500: 'rgb(217, 83, 79)',
      600: 'rgb(220, 38, 38)',
      700: 'rgb(185, 28, 28)',
      800: 'rgb(153, 27, 27)',
      900: 'rgb(127, 29, 29)',
      950: 'rgb(69, 10, 10)',
    },
    neutral: {
      0: 'rgb(255, 255, 255)',
      50: 'rgb(242, 242, 242)',
      100: 'rgb(229, 231, 235)',
      200: 'rgb(220, 220, 220)',
      300: 'rgb(192, 192, 192)',
      400: 'rgb(158, 158, 158)',
      500: 'rgb(128, 128, 128)',
      600: 'rgb(100, 116, 139)',
      700: 'rgb(51, 65, 85)',
      800: 'rgb(31, 41, 55)',
      900: 'rgb(30, 41, 59)',
      950: 'rgb(0, 0, 0)',
    },
  },
  semantic: {
    primary: {
      50: '{green.50}',
      100: '{green.100}',
      200: '{green.200}',
      300: '{green.300}',
      400: '{green.400}',
      500: '{green.500}',
      600: '{green.600}',
      700: '{green.700}',
      800: '{green.800}',
      900: '{green.900}',
      950: '{green.950}',
    },
    text: {
      primary: '{neutral.700}',
      secondary: '{neutral.800}',
      muted: '{neutral.600}',
    },
    interaction: {
      selectionBg: 'color-mix(in srgb, {primary.500} 8%, transparent)',
      selectionBorder: 'color-mix(in srgb, {primary.500} 28%, transparent)',
      focusRing: 'color-mix(in srgb, {primary.500} 55%, transparent)',
    },
    panel: {
      bg: 'color-mix(in srgb, {surface.0} 92%, transparent)',
    },
    gradient: {
      brand: 'linear-gradient(135deg, {primary.500} 0%, {primary.700} 100%)',
      secondary: 'linear-gradient(135deg, {orange.500} 0%, {orange.600} 100%)',
    },
    border: {
      subtle: 'color-mix(in srgb, {neutral.950} 10%, transparent)',
      muted: '{neutral.100}',
      default: '{neutral.200}',
      brand: '{primary.100}',
      'brand-strong': '{primary.500}',
      secondary: '{orange.500}',
      'secondary-accent': '{orange.600}',
      danger: '{red.500}',
    },
    shadow: {
      soft: 'color-mix(in srgb, {primary.700} 22%, transparent)',
      heavy: 'color-mix(in srgb, {primary.700} 45%, transparent)',
      'brand-color': 'color-mix(in srgb, {primary.700} 18%, transparent)',
      'muted-xs': '0 0 0.5rem {border.subtle}',
      'muted-sm': '0 0.5rem 1rem -0.5rem {shadow.soft}',
      'muted-md':
        '0 0.75rem 1.75rem -0.75rem {shadow.heavy}, 0 0.25rem 0.75rem -0.25rem {shadow.soft}',
      'muted-lg':
        '0 1.125rem 2.5rem -2.125rem {shadow.heavy}, 0 0.375rem 1.125rem -0.875rem {shadow.soft}',
      'muted-xl':
        '0 1.25rem 2.75rem -2.125rem {shadow.heavy}, 0 0.625rem 1.375rem -1.125rem {shadow.soft}',
      'brand-xs': '0 0 0.5rem {shadow.brand-color}',
      'brand-sm': '0 0.5rem 1rem -0.5rem {shadow.brand-color}',
      'brand-md':
        '0 0.75rem 1.75rem -0.75rem {shadow.brand-color}, 0 0.25rem 0.75rem -0.25rem {shadow.brand-color}',
      'brand-lg':
        '0 1.125rem 2.5rem -2.125rem {shadow.brand-color}, 0 0.375rem 1.125rem -0.875rem {shadow.brand-color}',
      'brand-xl':
        '0 1.25rem 2.75rem -2.125rem {shadow.brand-color}, 0 0.625rem 1.375rem -1.125rem {shadow.brand-color}',
    },
    colorScheme: {
      light: {
        primary: {
          color: '{primary.500}',
          contrastColor: '{neutral.0}',
          hoverColor: '{primary.700}',
          activeColor: '{primary.800}',
        },
        content: {
          color: '{neutral.700}',
        },
        highlight: {
          background: '{interaction.selectionBg}',
          focusBackground: '{interaction.selectionBg}',
          color: '{neutral.700}',
          focusColor: '{neutral.800}',
        },
        surface: {
          0: '{neutral.0}',
          50: '{neutral.50}',
          100: '{neutral.100}',
          200: '{neutral.200}',
          300: '{neutral.300}',
          400: '{neutral.400}',
          500: '{neutral.500}',
          600: '{neutral.600}',
          700: '{neutral.700}',
          800: '{neutral.800}',
          900: '{neutral.900}',
          950: '{neutral.950}',
        },
        formField: {
          background: '{surface.0}',
          disabledBackground: '{surface.50}',
          borderColor: '{border.default}',
          hoverBorderColor: '{border.muted}',
          focusBorderColor: '{primary.500}',
          color: '{neutral.700}',
          disabledColor: '{surface.300}',
          placeholderColor: '{surface.600}',
          invalidBorderColor: '{red.500}',
        },
      },
    },
  },
  /**
   * Design-token overrides (no global CSS needed).
   *
   * These mirror `ButtonComponent` styles so PrimeNG buttons
   * can be used directly (via `severity="primary" | "secondary"`).
   */
  components: {
    inputtext: {
      css: () => `
        .p-inputtext {
          width: 100%;
        }
      `,
    },
    password: {
      css: () => `
        .p-password {
          width: 100%;
        }
      `,
    },
    datatable: {
      root: {
        borderColor: 'transparent',
      },
      header: {
        borderColor: '{border.subtle}',
      },
      headerCell: {
        background: '{primary.700}',
        color: '{neutral.0}',
        hoverBackground: '{primary.800}',
        hoverColor: '{neutral.0}',
        selectedBackground: '{primary.800}',
        selectedColor: '{neutral.0}',
        borderColor: '{border.subtle}',
      },
      columnTitle: {
        fontWeight: '650',
      },
      row: {
        hoverBackground: '{primary.50}',
      },
      bodyCell: {
        borderColor: '{border.subtle}',
      },
      sortIcon: {
        color: '{neutral.0}',
        hoverColor: '{neutral.0}',
      },
      css: () => `
        .p-datatable {
          background-color: var(--primeng-neutral-0);
          border-radius: 0.75rem;
          overflow: hidden;
        }

        .p-datatable .p-datatable-thead > tr > th,
        .p-datatable .p-datatable-tbody > tr > td {
          font-size: 0.875rem;
          line-height: 1.25rem;
          font-weight: 400;
        }

        .p-datatable .p-datatable-emptymessage > td,
        .p-datatable .p-datatable-empty-message > td {
          text-align: center;
        }
      `,
    },
    paginator: {
      root: {
        background: '{surface.0}',
        borderRadius: '0.75rem',
        padding: '0.75rem 1rem',
        color: '{neutral.800}',
      },
      navButton: {
        background: 'transparent',
        hoverBackground: '{surface.50}',
        selectedBackground: '{primary.700}',
        color: '{neutral.800}',
        hoverColor: '{neutral.800}',
        selectedColor: '{neutral.0}',
        width: '2rem',
        height: '2rem',
        borderRadius: '0.375rem',
        focusRing: {
          width: '2px',
          style: 'solid',
          color: '{primary.500}',
          offset: '2px',
          shadow: 'none',
        },
      },
      currentPageReport: {
        color: '{neutral.800}',
      },
      css: () => `
        .p-paginator {
          margin-top: 0.625rem;
          font-size: 0.875rem;
          box-shadow:
            0 1px 3px 0 var(--primeng-border-subtle),
            0 1px 2px -1px var(--primeng-border-subtle);
        }

        .p-paginator .p-paginator-pages .p-paginator-page,
        .p-paginator .p-paginator-first,
        .p-paginator .p-paginator-prev,
        .p-paginator .p-paginator-next,
        .p-paginator .p-paginator-last {
          border: 1px solid transparent;
          transition: all 0.15s ease;
        }

        .p-paginator .p-paginator-pages .p-paginator-page.p-paginator-page-selected {
          border-color: var(--primeng-primary-700);
          font-weight: 600;
        }

        .p-paginator .p-paginator-pages .p-paginator-page.p-paginator-page-selected:hover {
          background-color: var(--primeng-primary-800);
          border-color: var(--primeng-primary-800);
        }

        .p-paginator .p-paginator-first.p-disabled,
        .p-paginator .p-paginator-prev.p-disabled,
        .p-paginator .p-paginator-next.p-disabled,
        .p-paginator .p-paginator-last.p-disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }

        .p-paginator .p-paginator-rpp-dropdown {
          border: 1px solid var(--primeng-border-default);
          min-height: 2rem;
        }

        .p-paginator .p-paginator-rpp-dropdown:hover {
          border-color: var(--primeng-neutral-600);
        }

        .p-paginator .p-paginator-rpp-dropdown:focus {
          outline: 2px solid var(--primeng-primary-500);
          outline-offset: 2px;
          border-color: var(--primeng-primary-500);
        }

        .p-paginator .p-paginator-rpp-dropdown .p-select-label {
          padding: 0.25rem 0.5rem;
        }
      `,
    },

    tooltip: {
      root: {
        maxWidth: '100%',
        gutter: '0',
        padding: '0.35rem 0.5rem',
        shadow: 'none',
      },
      colorScheme: {
        light: {
          root: {
            background: '{surface.0}',
            color: '{neutral.900}',
          },
        },
      },
      css: () => `
        .p-tooltip .p-tooltip-text {
          font-size: 0.75rem;
          width: 100%;
          height: 100%;
          border: 1px solid var(--primeng-border-default);
        }

        .p-tooltip .p-tooltip-arrow {
          display: none;
        }
      `,
    },
    button: {
      root: {
        gap: '0.5rem',
        borderRadius: '0.75rem',
        roundedBorderRadius: '0.75rem',
        paddingX: '1.25rem',
        paddingY: '0.6rem',
        label: {
          fontWeight: 'var(--ap-font-weight-normal)',
        },
        raisedShadow: 'none',
        // Avoid first-render morphing when lazy route/theme tokens settle.
        transitionDuration: '0s',
        // Button default sizing: PrimeUI uses sm/lg for font size.
        sm: {
          fontSize: '0.875rem',
          paddingX: '1.25rem',
          paddingY: '0.6rem',
        },
      },
      colorScheme: {
        light: {
          root: {
            primary: {
              // Matches `.primary-button` (neutral background + teal border).
              background: '{surface.50}',
              hoverBackground: '{surface.600}',
              activeBackground: '{surface.800}',
              borderColor: '{border.default}',
              hoverBorderColor: '{border.default}',
              activeBorderColor: '{border.default}',
              color: '{neutral.700}',
              hoverColor: '{primary.contrast.color}',
              activeColor: '{primary.contrast.color}',
              focusRing: {
                // Single source of truth: semantic primary.
                color: '{primary.color}',
                shadow: 'none',
              },
            },
            secondary: {
              // Secondary severity mapped to overridden orange primitive scale.
              background: '{orange.500}',
              hoverBackground: '{orange.600}',
              activeBackground: '{orange.700}',
              borderColor: 'transparent',
              hoverBorderColor: 'transparent',
              activeBorderColor: 'transparent',
              color: '{neutral.0}',
              hoverColor: '{neutral.0}',
              activeColor: '{neutral.0}',
              focusRing: {
                color: '{orange.500}',
                shadow: 'none',
              },
            },
            danger: {
              // Danger severity mapped to overridden red primitive scale.
              background: '{red.500}',
              hoverBackground: '{red.600}',
              activeBackground: '{red.700}',
              borderColor: '{red.500}',
              hoverBorderColor: '{red.600}',
              activeBorderColor: '{red.700}',
              color: '{neutral.0}',
              hoverColor: '{neutral.0}',
              activeColor: '{neutral.0}',
              focusRing: {
                color: '{red.500}',
                shadow: 'none',
              },
            },
            contrast: {
              // Reused as app tertiary variant.
              background: '{surface.0}',
              hoverBackground: '{surface.50}',
              activeBackground: '{surface.100}',
              borderColor: '{border.default}',
              hoverBorderColor: '{border.default}',
              activeBorderColor: '{primary.200}',
              color: '{neutral.700}',
              hoverColor: '{neutral.700}',
              activeColor: '{neutral.800}',
              focusRing: {
                color: '{primary.500}',
                shadow: 'none',
              },
            },
          },
        },
      },
    },
  },
});

export default PrimengPreset;
